import axios from 'axios';
import { openKnotsCopy } from '../../utils/constants/openKnotsLabel';

const readModuleValue = (mod, name) => {
    if (!mod || (typeof mod !== 'object' && typeof mod !== 'function')) {
        return undefined;
    }
    try {
        return mod[name];
    } catch {
        return undefined;
    }
};

export const resolveOpenKnotsMount = (mountModule) => {
    const seen = new Set();
    const queue = [mountModule];

    while (queue.length) {
        const current = queue.shift();
        if (!current || seen.has(current)) {
            continue;
        }
        if (typeof current === 'function') {
            return current;
        }
        if (typeof current !== 'object') {
            continue;
        }
        seen.add(current);

        const namedMount = readModuleValue(current, 'mount');
        if (typeof namedMount === 'function') {
            return namedMount;
        }

        const names = Object.getOwnPropertyNames(current);
        for (let i = 0; i < names.length; i += 1) {
            const value = readModuleValue(current, names[i]);
            if (typeof value === 'function' && (names[i] === 'default' || names[i] === 'mount')) {
                return value;
            }
            if (value && typeof value === 'object' && value !== current) {
                queue.push(value);
            }
        }
    }
    return undefined;
};

export const loadOpenKnotsMountModule = async () => {
    const imported = await import('openKnotsUI/mount');
    const fromImport = resolveOpenKnotsMount(imported);
    if (typeof fromImport === 'function') {
        return fromImport;
    }

    const container = typeof window !== 'undefined' ? window.openKnotsUI : null;
    if (container && typeof container.get === 'function') {
        const factory = await container.get('./mount');
        const fromContainer = resolveOpenKnotsMount(typeof factory === 'function' ? factory() : factory);
        if (typeof fromContainer === 'function') {
            return fromContainer;
        }
    }

    const keys = describeMountModule(imported);
    throw new TypeError(`Open Knots mount export is not a function (module keys: ${keys || 'none'})`);
};

export const applyRemotePublicPath = (remoteEntryUrl) => {
    const remoteOrigin = remoteEntryUrl.replace(/\/[^/]*$/, '/');
    if (typeof window !== 'undefined') {
        window.__OPENKNOTS_PUBLIC_PATH__ = remoteOrigin;
    }
};

export const loadUserDataFromStorage = (ctx) => {
    try {
        const rawUserData = localStorage.getItem('userData');
        const parsedUserData = rawUserData ? JSON.parse(rawUserData) : null;
        if (parsedUserData?.user_organizations) {
            ctx.handleUserData(parsedUserData);
            return parsedUserData;
        }
    } catch {
        // ignore
    }
    return null;
};

export const loadUserDataFromApi = async (ctx, baseUrlUser) => {
    if (!baseUrlUser) {
        return null;
    }
    try {
        const idToken = localStorage.getItem('idToken');
        if (!idToken) {
            return null;
        }
        const res = await axios.post(
            `${baseUrlUser}/basicusers`,
            { idToken },
            {
                headers: {
                    'content-type': 'application/json',
                },
            },
        );
        if (res.status === 200 && res.data?.payload) {
            ctx.handleUserData(res.data.payload);
            return res.data.payload;
        }
    } catch {
        // ignore
    }
    return null;
};

export const buildOpenKnotsUser = (userData) => ({
    username: userData?.name || userData?.email || 'User',
    name: userData?.name,
    email: userData?.primary_email,
    user_ulid: userData?.user_ulid,
    organizations: userData?.user_organizations,
    role: userData?.user_organizations?.[0]?.user_org_role_maps?.[0]?.platform_role,
});

export const describeMountModule = (mountModule) => {
    if (mountModule && typeof mountModule === 'object') {
        return Object.keys(mountModule).join(', ');
    }
    return typeof mountModule;
};

export const resolveUserData = async (ctx, baseUrlUser) => {
    let userData = ctx.userData;
    if (!userData) {
        userData = loadUserDataFromStorage(ctx);
    }
    if (!userData) {
        userData = await loadUserDataFromApi(ctx, baseUrlUser);
    }
    return userData;
};

export const loadAndMountOpenKnots = async ({
    containerRef,
    ctx,
    baseUrlUser,
    knotTabVisibility,
    navigate,
    setStatus,
    setErrorMsg,
    setShowLoadingOverlay,
    unmountRef,
    mountState,
    timers,
    loadingDelayMs,
}) => {
    if (mountState.completed || !containerRef.current) {
        return;
    }

    try {
        setStatus('loading');
        setErrorMsg('');
        setShowLoadingOverlay(false);
        timers.loadingOverlayTimer = setTimeout(() => {
            if (!mountState.completed) {
                setShowLoadingOverlay(true);
            }
        }, loadingDelayMs);
        timers.loadTimeout = setTimeout(() => {
            if (!mountState.completed) {
                setStatus('error');
                setErrorMsg(openKnotsCopy('{openKnots} is taking too long to load. Please try again.'));
            }
        }, 15000);

        const remoteEntryUrl =
            process.env.REACT_APP_OPENKNOTS_REMOTE_URL || 'http://localhost:3001/remoteEntry.js';
        applyRemotePublicPath(remoteEntryUrl);

        const mount = await loadOpenKnotsMountModule();

        const session = await ctx.amplifyAuth().currentSession();
        const token = session.accessToken.getJwtToken();
        const userData = await resolveUserData(ctx, baseUrlUser);
        if (!userData) {
            setStatus('error');
            setErrorMsg('Unable to load user details. Please refresh and try again.');
            setShowLoadingOverlay(false);
            return;
        }

        const unmount = mount(containerRef.current, {
            user: buildOpenKnotsUser(userData),
            token,
            apiBaseUrls: {
                rfq: process.env.REACT_APP_RFQ_MANAGEMENT,
                txn: process.env.REACT_APP_TRANSACTIONS_MANAGEMENT,
            },
            knotTabVisibility,
            onNavigate: (path) => navigate(path),
            onUnauthorized: () => ctx.onLogout(),
        });

        unmountRef.current = unmount;
        mountState.completed = true;
        setStatus('ready');
        setShowLoadingOverlay(false);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load Open Knots UI:', error);
        setStatus('error');
        setErrorMsg(openKnotsCopy('Failed to load {openKnots} — remote unavailable'));
        setShowLoadingOverlay(false);
    } finally {
        clearTimeout(timers.loadingOverlayTimer);
        if (timers.loadTimeout) {
            clearTimeout(timers.loadTimeout);
        }
    }
};
